import { ErrorMessage, Input, Space } from '@/components';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';
import { isTextFilled, transformFallbackValue } from './utils';

type Props<T extends FieldValues> = {
  name: Path<T>;
  autocompleteKey?: string; // change autocomplete even if the name is the same
  label?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  disabled?: boolean;
  fallbackValue?: '' | null;
  hidden?: boolean;
  maxLength?: number;
  placeholder?: string;
  addonAfter?: ReactNode;
  width?: CSSProperties['width'];
  wrapperStyle?: CSSProperties;
  marginBottom?: number;
  hint?: string;
  shouldCoexistOnChange?: boolean;
  labelSize?: FieldLabelSize;
  allowClear?: boolean;
  additionalLabelComponent?: ReactNode;
} & Pick<ComponentProps<typeof Input>, 'onChange'>;

export const TextInputField = <T extends FieldValues>({
  name,
  autocompleteKey,
  label,
  control,
  errors,
  required,
  disabled,
  fallbackValue,
  hidden,
  maxLength,
  placeholder,
  addonAfter,
  width,
  marginBottom,
  hint,
  shouldCoexistOnChange = false,
  onChange,
  labelSize = 'medium',
  allowClear,
  wrapperStyle,
  additionalLabelComponent,
}: Props<T>) => {
  const transformedFallbackValue = transformFallbackValue(
    fallbackValue,
    required,
  );

  return (
    <Form.Item
      style={{ marginBottom, ...wrapperStyle }}
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
            additionalComponent={additionalLabelComponent}
          />
        )
      }
      hidden={hidden}
    >
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: fieldOnChange, onBlur, ...field } }) => (
          <Space.Compact style={{ width }} block>
            <Input
              {...field}
              onChange={(e) => {
                if (shouldCoexistOnChange || !onChange) {
                  if (isTextFilled(e.target.value)) {
                    fieldOnChange(e.target.value || transformedFallbackValue);
                  }
                }
                onChange?.(e);
              }}
              onBlur={(e) => {
                fieldOnChange(
                  e.target.value.trim() || transformedFallbackValue,
                );
                onBlur();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  fieldOnChange(
                    e.currentTarget.value.trim() || transformedFallbackValue,
                  );
                }
              }}
              allowClear={allowClear}
              name={autocompleteKey ?? field.name}
              placeholder={placeholder}
              disabled={disabled}
              addonAfter={addonAfter}
              maxLength={maxLength}
            />
          </Space.Compact>
        )}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
